import { Injectable } from '@angular/core'
import { SharedModule } from '@tv3/shared/shared.module'
import {
  AlertDialogService,
  Channel,
  ChannelNameTokens,
  ConfirmDialogService,
  InfoDialogService,
  isSomething,
  Rental,
  selectOnce,
  Toaster,
} from '@tokeet-frontend/tv3-platform'
import { concatMap, filter, map, mapTo, switchMap, take, tap, toArray } from 'rxjs/operators'
import { AuthService } from '@tv3/services/auth.service'
import { Actions, ofType } from '@ngrx/effects'
import { select, Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import * as R from 'ramda'
import {
  AddExportedCalendarConnection,
  DeleteCalendarConnections,
  DeleteExportedCalendarConnection,
  DeleteImportedCalendarConnection,
  ImportBookingsFromConnection,
  ImportBookingsFromConnectionComplete,
  ImportBookingsFromConnectionFailed,
  LinkConnectionWithRental,
  LoadConnectionsByChannel,
  ManualRefreshImportedCalendarConnection,
  ManualRefreshImportedCalendarConnections,
  PushAvailabilityToConnection,
  PushAvailabilityToConnectionComplete,
  PushAvailabilityToConnectionForWizard,
  PushRatesToConnection,
  UnlinkConnectionsWithRental,
  UnlinkConnectionWithRental,
} from '@tv3/store/connection/connection.actions'
import { ConnectionView } from '@tv3/store/connection/connection.view'
import { SelectRateToPushDialogService } from '@tv3/containers/channels/channel-connect/dialogs/select-rate-to-push-dialog/select-rate-to-push-dialog.service'
import { SelectRentalDialogService } from '@tv3/shared/dialogs/select-rental-dialog/select-rental-dialog.service'
import { EMPTY, forkJoin, Observable, of, throwError } from 'rxjs'
import { ConnectionService } from '@tv3/store/connection/connection.service'
import * as moment from 'moment'
import { ActionFailed } from '@tokeet-frontend/tv3-platform'
import {
  selectAvailableRentalForNewAPIConnection,
  selectAvailableRentalForNewExportedCalendar,
  selectConnectionById,
} from '@tv3/store/connection/connection.selectors'
import {
  AddExportedCalendarConnectionPayload,
  PushRatesToApiConnectionPayload,
} from '@tv3/store/connection/connection.types'
import { RategenieService } from '@tv3/services/rategenie.service'
import { selectAccountPreference } from '@tv3/store/preferences/preferences.selectors'
import { Preference } from '@tv3/store/preferences/preferences.model'
import { UpdatePreferences } from '@tv3/store/preferences/preferences.actions'
import { RentalRateService } from '@tv3/services/rental-rate.service'
import { v4 as uuid } from 'uuid'
import {
  ChannelImportStatus,
  Connection,
  getRealChannelId,
  scheduledConnectionChanges,
} from '@tv3/store/connection/connection.model'
import {
  PushAvailabilityToCtripListing,
  PushAvailabilityToTiketListing,
  PushRatesToCtripListing,
  PushRatesToTiketListing,
} from '@tokeet-frontend/channels'
import { SelectRateToPushDialogConfig } from './dialogs/select-rate-to-push-dialog/select-rate-to-push-dialog.component'
import { OpenRentalOverlay } from '@tv3/store/overlay/overlay.actions'
import { RentalDialogTab } from '@tv3/containers/rentals/overlays/rental-overlay/rental-overlay.component'

@Injectable({
  providedIn: 'root',
})
export class ConnectionHelperService {
  constructor(
    private auth: AuthService,
    private store: Store<fromRoot.State>,
    private toaster: Toaster,
    private alertDialog: AlertDialogService,
    private confirmDialog: ConfirmDialogService,
    private infoDialog: InfoDialogService,
    private selectRentalDialog: SelectRentalDialogService,
    private selectRateToPushDialog: SelectRateToPushDialogService,
    private rategenieService: RategenieService,
    private rentalRateService: RentalRateService,
    private connectionService: ConnectionService,
    private actions: Actions
  ) {}

  private bookingImportStatusesByProperty(statuses: (ChannelImportStatus | null)[]): ChannelImportStatus[] {
    const byProperty = new Map<string, ChannelImportStatus>()

    statuses.forEach((status, index) => {
      if (!status) {
        return
      }

      const key = isSomething(status.propertyId)
        ? `property:${status.propertyId}`
        : status.job_id
        ? `job:${status.job_id}`
        : `status:${index}`
      // Multiple selected room rows can refer to one property-wide Booking.com job.
      // Retain the first result, which represents the user's import, rather than a
      // later duplicate run that only sees the just-created reservations as existing.
      if (!byProperty.has(key)) {
        byProperty.set(key, status)
      }
    })

    return Array.from(byProperty.values())
  }

  private bookingImportSuccessText(statuses: (ChannelImportStatus | null)[]): string {
    const propertyStatuses = this.bookingImportStatusesByProperty(statuses)
    const processed = propertyStatuses.reduce((sum, status) => sum + (status.processed ?? status.imported ?? 0), 0)
    const total = propertyStatuses.reduce((sum, status) => sum + (status.total || 0), 0)
    const created = propertyStatuses.reduce((sum, status) => sum + (status.created_count || 0), 0)
    const updated = propertyStatuses.reduce((sum, status) => sum + (status.updated_count || 0), 0)
    const alreadyExisting = propertyStatuses.reduce((sum, status) => sum + (status.already_existing_count || 0), 0)
    const warningCount = propertyStatuses.reduce(
      (sum, status) => sum + Math.max(status.errors?.length || 0, status.warning_count || 0),
      0
    )
    const reportedFailures = propertyStatuses.reduce(
      (sum, status) => sum + (status.errors?.filter((error) => error.error === 'import_failed').length || 0),
      0
    )
    const unprocessed = Math.max(0, total - processed)
    const reservationFailures = Math.max(reportedFailures, unprocessed)
    const detailWarnings = Math.max(0, warningCount - reservationFailures)
    const hasBreakdown = propertyStatuses.some(
      (status) =>
        isSomething(status.created_count) ||
        isSomething(status.updated_count) ||
        isSomething(status.already_existing_count)
    )

    if (!total) {
      return $localize`No reservations were found for this Booking.com property.`
    }

    const processedText = (() => {
      if (propertyStatuses.length === 1) {
        return processed < total
          ? $localize`Processed ${processed} of ${total} reservations for this Booking.com property.`
          : $localize`Processed ${processed} reservations for this Booking.com property.`
      }
      return processed < total
        ? $localize`Processed ${processed} of ${total} reservations for ${propertyStatuses.length} Booking.com properties.`
        : $localize`Processed ${processed} reservations for ${propertyStatuses.length} Booking.com properties.`
    })()

    const resultText = hasBreakdown
      ? `${processedText} ${$localize`${created} created, ${updated} updated, and ${alreadyExisting} already existed.`}`
      : processedText

    const warnings: string[] = []
    if (reservationFailures) {
      warnings.push(
        reservationFailures === 1
          ? $localize`1 reservation could not be processed.`
          : $localize`${reservationFailures} reservations could not be processed.`
      )
    }
    if (detailWarnings) {
      warnings.push(
        detailWarnings === 1
          ? $localize`1 reservation detail warning was reported.`
          : $localize`${detailWarnings} reservation detail warnings were reported.`
      )
    }

    return warnings.length ? `${resultText} ${warnings.join(' ')}` : resultText
  }

  private bookingImportHasWarnings(statuses: (ChannelImportStatus | null)[]): boolean {
    return this.bookingImportStatusesByProperty(statuses).some((status) => {
      const processed = status.processed ?? status.imported ?? 0
      return (status.total || 0) > processed || Math.max(status.errors?.length || 0, status.warning_count || 0) > 0
    })
  }

  tryToDisplayConnectionWarning(connections: ConnectionView[]) {
    const showWarning = !!R.find((c) => c.warning, connections)
    if (!showWarning) {
      return
    }

    this.store
      .pipe(
        select(selectAccountPreference),
        take(1),
        switchMap((pref: Preference) => {
          if (pref && pref.disableChannelConnectionsWarning) {
            return of(true)
          }
          return this.infoDialog
            .open({
              title: $localize`Connection Warning`,
              body: $localize`Your have either Disabled, Stale, or Pending connections. Please fix these connections as soon as possible.
          AdvanceCM will not sync with your connections while they are in this status.`,
              extra: $localize`Disable this notification forever.`,
            })
            .pipe(
              filter((result) => isSomething(result)),
              tap(({ extra }) => {
                if (extra) {
                  this.store.dispatch(
                    UpdatePreferences({ id: pref.id, form: { disable_channel_connections_warning: 1 } })
                  )
                }
              })
            )
        }),
        take(1)
      )
      .subscribe()
  }

  setUpExportCalendar(channel: Channel) {
    return this.store.pipe(
      select(selectAvailableRentalForNewExportedCalendar, { name: channel.name }),
      take(1),
      switchMap((rentals: Rental[]) => {
        return this.selectRentalDialog.open({
          help: $localize`Please select the AdvanceCM rental whose iCal URL you would like to export. You will be given instructions on how to export
      the URL to this channel. You can undo this setting later if you wish.`,
          rentals,
        })
      }),
      switchMap((rental: Rental) => {
        return Observable.create((observer) => {
          const refId = Date.now().toString(36).toLowerCase()
          const baseURL = `${location.protocol}//${location.hostname}${location.port ? ':' + location.port : ''}`
          const url = `${baseURL}/pages/${channel.infoPage}?accountId=${this.auth.user.account}&rentalId=${rental.id}&ref_id=${refId}`
          // openWindow(url);

          const calendarUrl = `https://calendars.tokeet.com/calendar/rental/${rental.account}/${rental.id}?ref_id=${refId}`
          // @ts-ignore
          // window.successCallback = (channelRentalId, query) => {
          const payload = {
            rental_id: rental.id,
            id: channel.id,
            name: channel.name,
            agent: channel.agent,
            type: channel.type,
            rental_name: rental.name || '',
            channel_rental_id: `${channel.id}-${rental.id}`,
            ref_id: refId,
            url: calendarUrl,
          } as AddExportedCalendarConnectionPayload
          this.store.dispatch(AddExportedCalendarConnection({ payload }))

          observer.next(calendarUrl)
          observer.complete()
          // };
        })
      }),
      switchMap((url) => {
        return this.infoDialog
          .open({
            title: $localize`Exported Calendar`,
            body: `<p style="word-break: break-all;">Below is your exported calendar url: <br/> <a href="${url}" target="_blank">${url}</a></p>`,
            closeText: $localize`Done`,
          })
          .pipe(mapTo(url))
      }),
      take(1)
    )
  }

  importAllBookings(channelName: ChannelNameTokens, connectionViews: ConnectionView[]) {
    connectionViews = R.filter((conn) => !!conn.status && !!conn.rental, connectionViews)
    if (!connectionViews.length) {
      this.alertDialog.open({
        title: $localize`Warning`,
        body: $localize`No connection(s) to import.`,
      })
      return
    }

    // Booking.com imports are property-wide even when the selection contains one row
    // per room. Queue exactly one job per property so the same reservations are not
    // immediately processed again and summarized from the duplicate run.
    const importConnections =
      channelName === ChannelNameTokens.BookingAPI
        ? Array.from(
            connectionViews
              .reduce((byProperty, connection, index) => {
                const key = isSomething(connection.propertyId)
                  ? `property:${connection.propertyId}`
                  : `connection:${connection.id || index}`
                if (!byProperty.has(key)) {
                  byProperty.set(key, connection)
                }
                return byProperty
              }, new Map<string, ConnectionView>())
              .values()
          )
        : connectionViews

    this.confirmDialog
      .confirm({
        title: $localize`Import bookings from channel?`,
        body:
          `This will import your bookings from the selected channel into AdvanceCM. AdvanceCM will only import bookings
            that are not already saved. However, this operation may miss some older bookings.
            ` +
          `<br><br>` +
          $localize`Are you sure you want to import?`,
      })
      .pipe(
        switchMap(() => of(...importConnections)),
        concatMap((connection) => {
          return this.connectionService.importBookings(channelName, connection.channelId, connection).pipe(
            tap(() =>
              this.store.dispatch(
                ImportBookingsFromConnectionComplete({
                  update: {
                    id: connection.id,
                    changes: scheduledConnectionChanges(channelName, { lastavailpull: moment.utc().unix() }),
                  },
                })
              )
            ),
            switchMap((response) => {
              if (channelName !== ChannelNameTokens.BookingAPI) {
                return of(null)
              }

              if (!response.job_id) {
                return throwError(new Error('Booking.com import did not return a job id.'))
              }

              return this.connectionService
                .waitForImportResult(channelName, connection.channelId, response.job_id)
                .pipe(
                  switchMap((status) =>
                    status?.status === 'done'
                      ? of(status)
                      : throwError(status || new Error('Booking.com import status could not be confirmed.'))
                  )
                )
            })
          )
        }),
        toArray()
      )
      .subscribe(
        (statuses: (ChannelImportStatus | null)[]) => {
          if (channelName === ChannelNameTokens.BookingAPI) {
            const text = this.bookingImportSuccessText(statuses)
            if (this.bookingImportHasWarnings(statuses)) {
              this.toaster.warning(text)
            } else {
              this.toaster.success(text)
            }
            this.store.dispatch(
              LoadConnectionsByChannel({
                channelName,
                channelId: getRealChannelId(importConnections[0].channelId),
              })
            )
          } else {
            this.toaster.success($localize`Bookings imported into AdvanceCM successfully.`)
          }
        },
        (error) => {
          if (channelName === ChannelNameTokens.BookingAPI) {
            this.toaster.error($localize`Booking import failed. Please try again.`)
          }
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  importBookings(channelName: ChannelNameTokens, connection: Connection): Observable<any> {
    const requestId = uuid()
    return this.confirmDialog
      .confirm({
        title: $localize`Import bookings from channel?`,
        body:
          $localize`This will import your bookings from the selected channel into AdvanceCMAdvanceCM. AdvanceCM will only import bookings
            that are not already saved. However, this operation may miss some older bookings.
            ` +
          `<br><br>` +
          $localize`Are you sure you want to import?`,
      })
      .pipe(
        switchMap(() => this.store.pipe(select(selectConnectionById, { id: connection.id }))),
        tap((connection) => {
          this.store.dispatch(
            ImportBookingsFromConnection({
              connId: connection.id,
              channelId: connection.channelId,
              channelName: channelName,
              data: connection,
              requestId,
            })
          )
        }),
        take(1),
        // TV4-5905: BDC imports run as a tracked backend job; follow it and report the
        // recorded outcome instead of treating the schedule as the result.
        switchMap((conn) =>
          channelName === ChannelNameTokens.BookingAPI
            ? this.followBookingImportJob(channelName, conn, requestId)
            : of(conn)
        )
      )
  }

  // QA helper (hidden on production): pulls the channel's booking event feed
  // (new / modified / cancelled bookings) and applies it — which the
  // summary-based importBookings above cannot do. Stage has no cron running
  // this pull, so testers trigger it manually right after changing a booking
  // on the channel.
  importNewBookings(channelName: ChannelNameTokens, connection: Connection) {
    this.confirmDialog
      .confirm({
        title: $localize`Import booking events from channel?`,
        body:
          $localize`This will fetch the latest booking events (new, modified and cancelled bookings) from the
            channel and apply them to AdvanceCM. The channel delivers each event only once, so run this
            right after making the change on the channel.` +
          `<br><br>` +
          $localize`Are you sure you want to import?`,
      })
      .pipe(switchMap(() => this.connectionService.importNewBookings(channelName, connection.channelId, connection)))
      .subscribe(
        () => {
          this.toaster.success($localize`Booking events imported into AdvanceCM.`)
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  // Dialog-free variant of importBookings for the listing wizard's automated chain
  // (TV4-5906): dispatches the import and emits the tracked job's recorded states.
  importBookingsUnattended(
    channelName: ChannelNameTokens,
    connection: Connection
  ): Observable<ChannelImportStatus | null> {
    const requestId = uuid()
    return this.store.pipe(
      select(selectConnectionById, { id: connection.id }),
      take(1),
      tap((conn) => {
        this.store.dispatch(
          ImportBookingsFromConnection({
            connId: conn.id,
            channelId: conn.channelId,
            channelName: channelName,
            data: conn,
            requestId,
          })
        )
      }),
      switchMap((conn) =>
        channelName === ChannelNameTokens.BookingAPI
          ? this.followBookingImportJob(channelName, conn, requestId)
          : of(null)
      )
    )
  }

  // Emits every recorded state of the connection's bookings-import job (null while none is
  // on record yet, or when an older backend records none at all) and completes on the
  // terminal state, toasting the outcome once. The emissions let the BDC listing wizard
  // render live property-wide reservation processing progress (TV4-5905).
  private followBookingImportJob(
    channelName: ChannelNameTokens,
    connection: Connection,
    requestId: string
  ): Observable<ChannelImportStatus | null> {
    return this.actions.pipe(
      ofType(ImportBookingsFromConnectionComplete, ImportBookingsFromConnectionFailed),
      filter((action) => action.requestId === requestId),
      take(1),
      switchMap((action) => {
        if (action.type === ImportBookingsFromConnectionFailed.type) {
          return of({ status: 'failed', error: 'enqueue_failed' } as ChannelImportStatus)
        }

        const jobId = 'jobId' in action ? action.jobId : undefined
        return jobId ? this.connectionService.pollImportStatus(channelName, connection.channelId, jobId) : EMPTY
      }),
      tap((status) => {
        if (status?.status === 'failed' && status.error !== 'enqueue_failed') {
          this.toaster.error($localize`Booking import failed. Please try again.`)
        } else if (status?.status === 'done') {
          const text = this.bookingImportSuccessText([status])
          if (this.bookingImportHasWarnings([status])) {
            this.toaster.warning(text)
          } else {
            this.toaster.success(text)
          }
          this.store.dispatch(
            LoadConnectionsByChannel({ channelName, channelId: getRealChannelId(connection.channelId) })
          )
        }
      })
    )
  }

  pushAllAvailability(channelName: ChannelNameTokens, connections: ConnectionView[]) {
    connections = R.filter((conn) => !!conn.status && !!conn.rental, connections)
    if (!connections.length) {
      this.alertDialog.open({
        title: $localize`Warning`,
        body: $localize`No connection(s) to push.`,
      })
      return
    }

    this.confirmDialog
      .confirm({
        title: $localize`Push availability to channel?`,
        body:
          $localize`This will push your rental(s) availability from AdvanceCM to the selected channel. The channel information will be overwritten
        with your AdvanceCM details. ` +
          `<br>` +
          $localize`Please ensure your AdvanceCM information is up to date. ` +
          `<br><br>` +
          $localize` Are you sure you want to push?`,
      })
      .pipe(
        switchMap(() => of(...connections)),
        concatMap((connection) => {
          return this.connectionService.pushAvailability(channelName, connection.channelId, connection).pipe(
            tap(() =>
              this.store.dispatch(
                PushAvailabilityToConnectionComplete({
                  update: {
                    id: connection.id,
                    changes: scheduledConnectionChanges(channelName, { lastavailpush: moment.utc().unix() }),
                  },
                })
              )
            )
          )
        }),
        toArray()
      )
      .subscribe(
        () => {
          // TV4-5909: the requests above only schedule asynchronous pushes -- don't claim
          // the channel already has the data.
          this.toaster.success(
            $localize`Availability updates scheduled. They may take a few minutes to reach the channel.`
          )
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  private _pushAvailability(connection: Connection): Observable<any> {
    return this.confirmDialog.confirm({
      title: $localize`Push availability to channel?`,
      body:
        $localize`This will push your rental availability from AdvanceCM to the selected channel. The channel information will be overwritten with
            your AdvanceCM details. ` +
        `<br>` +
        $localize`Please ensure your AdvanceCM information is up to date. ` +
        `<br><br>` +
        $localize`Are you sure you want to push?`,
    })
  }

  pushAvailability(channelName: ChannelNameTokens, connectionView: ConnectionView): Observable<any> {
    return this._pushAvailability(connectionView).pipe(
      switchMap(() => this.store.pipe(select(selectConnectionById, { id: connectionView.id }))),
      tap((connection) => {
        this.store.dispatch(
          PushAvailabilityToConnection({
            connId: connection.id,
            channelId: connection.channelId,
            channelName: channelName,
            data: connection,
          })
        )
      }),
      take(1)
    )
  }

  pushAvailabilityForTiketLike(connection: Connection, channelName: ChannelNameTokens) {
    return this._pushAvailability(connection).pipe(
      tap(() => {
        const payload = {
          connId: connection.id,
          roomTypeCode: connection.roomId as number,
          data: {
            rental_id: connection.rentalId,
            hotel_id: connection.propertyId as number,
          },
        }

        switch (channelName) {
          case ChannelNameTokens.Tiket:
            this.store.dispatch(PushAvailabilityToTiketListing(payload))
            break
          case ChannelNameTokens.Trip:
            this.store.dispatch(PushAvailabilityToCtripListing(payload))
            break
        }
      })
    )
  }

  pushAvailabilityForWizard(
    channelName: ChannelNameTokens,
    connection: Connection,
    tmpHoldEventId?: string
  ): Observable<any> {
    return this._pushAvailability(connection).pipe(
      switchMap(() => this.store.pipe(select(selectConnectionById, { id: connection.id }))),
      tap((connection) => {
        this.store.dispatch(
          PushAvailabilityToConnectionForWizard({
            connId: connection.id,
            channelId: connection.channelId,
            channelName: channelName,
            data: connection,
            tmpHoldEventId,
          })
        )
      }),
      take(1)
    )
  }

  pushRateForTiketLikeChannel(connection: Connection, channel: Channel, rental: Rental) {
    const doPushRates = (categories) => {
      return this.selectRateToPushDialog
        .open({
          channelId: connection.channelId,
          channelName: channel.name,
          channelFriendlyName: channel.friendlyName || 'channel',
          propertyId: connection.propertyId,
          roomId: connection.roomId,
          categories,
          rental,
        })
        .pipe(
          tap(({ category, channelRate }: { category: string; channelRate: any }) => {
            const payload = {
              connId: connection.id,
              roomTypeCode: connection.roomId as number,
              data: {
                rental_id: connection.rentalId,
                hotel_id: connection.propertyId as number,
                rate_plan_code: channelRate.id,
                category,
              },
            }

            switch (channel.name) {
              case ChannelNameTokens.Tiket:
                this.store.dispatch(PushRatesToTiketListing(payload))
                break
              case ChannelNameTokens.Trip:
                this.store.dispatch(PushRatesToCtripListing(payload))
                break
            }
          })
        )
    }

    return this.checkBeforePushRates(rental, connection).pipe(
      filter(isSomething),
      switchMap((categories) => doPushRates(categories))
    )
  }

  // Silent variant of checkBeforePushRates: reports what is missing instead of opening
  // the blocking alert. When all RateGenie syncs are enabled, rates are managed by
  // RateGenie and the rental is always considered ready.
  getRentalRatesReadiness(
    rental: Rental,
    connection: Connection
  ): Observable<{ categories: { id: string; name: string }[]; ready: boolean; missing: string[] }> {
    return forkJoin([
      this.rategenieService.checkAccountSync(),
      this.rategenieService.checkTokeetSync(),
      this.rategenieService.checkRentalSync(rental.id),
      this.rategenieService.checkChannelConnectionSync(
        rental.id,
        (connection.roomId || connection.propertyId) as string
      ),
    ]).pipe(
      map((syncs: any[]) => R.all((s) => s, syncs)),
      switchMap((allSyncEnabled) =>
        this.rentalRateService.getRateCategories(rental.id, true).pipe(
          map((categories) => {
            if (allSyncEnabled) {
              return { categories, ready: true, missing: [] }
            }
            const missing: string[] = []
            if (!rental.baseRate || !rental.baseRate.nightly) {
              missing.push($localize`Base rate (nightly) is required.`)
            }
            if (!isSomething(categories)) {
              missing.push($localize`At least one additional rate is required.`)
            }
            if (!rental.currency || !rental.currency.code) {
              missing.push($localize`You must specify a currency code.`)
            }
            return { categories, ready: !missing.length, missing }
          })
        )
      )
    )
  }

  checkBeforePushRates(rental: Rental, connection: Connection) {
    return this.getRentalRatesReadiness(rental, connection).pipe(
      switchMap(({ categories, ready }) => {
        if (!ready) {
          return this.alertDialog
            .openResult({
              title: $localize`Rental rate information incomplete.`,
              body:
                $localize`Please check your rental rate information.` +
                `<br>` +
                $localize`1. Base rate (nightly) is required.` +
                `<br>` +
                $localize`2. At least one additional rate is required.` +
                `<br>` +
                $localize`3. You must specify a currency code.`,
              extraButton: {
                text: 'Add Rates',
                icon: 'far fa-calculator',
                style: 'btn btn-secondary-info',
              },
            })
            .pipe(
              tap((res) => {
                if (res === 'extra') {
                  this.store.dispatch(OpenRentalOverlay({ rentalId: rental.id, activeTab: RentalDialogTab.Settings }))
                }
              }),
              mapTo([] as { id: string; name: string }[])
            )
        }

        return of(categories)
      })
    )
  }

  pushRatesForFeedConnection(channel: Channel, rental: Rental, connection: Connection): Observable<any> {
    return this.checkBeforePushRates(rental, connection).pipe(
      filter(isSomething),
      switchMap((categories: { id: string; name: string }[]) => {
        if (!categories.find((t) => t.id === 'default')) {
          return this.alertDialog
            .openResult({
              title: $localize`Warning!`,
              body: $localize`Default category rates are missing, please add default category rates.`,
            })
            .pipe(switchMap(() => EMPTY))
        }
        return of(categories)
      }),
      switchMap(() =>
        this.confirmDialog.confirm({
          title: $localize`Push Default Category Rates To Channel?`,
          body: $localize`Default category rates set for this rental in rates table will be pushed to this channel. Are you are sure you want to do this?`,
        })
      ),
      tap(() => {
        this.store.dispatch(
          PushRatesToConnection({
            connId: connection.id,
            channelId: connection.channelId,
            channelName: channel.name,
            data: {
              roomId: connection.roomId,
              rental_id: connection.rentalId,
              propertyId: connection.propertyId,
            },
          })
        )
      })
    )
  }

  getPushRatesForAPIConnectionPayload(
    channel: Channel,
    rental: Rental,
    connection: Connection,
    options?: Partial<SelectRateToPushDialogConfig>
  ): Observable<PushRatesToApiConnectionPayload | null> {
    if (!rental) {
      this.toaster.warning($localize`Cannot find the associated rental.`)
      return of(null)
    }

    const createAction = (categories: any[]) => {
      return this.selectRateToPushDialog
        .open({
          channelId: connection.channelId,
          channelName: channel.name,
          channelFriendlyName: channel.friendlyName || 'channel',
          propertyId: connection.propertyId,
          roomId: connection.roomId,
          categories,
          rental,
          ...options,
        })
        .pipe(
          map(({ category, channelRate }: { category: string; channelRate: any }) => ({
            category,
            rateid: channelRate.id,
            maxguest: channelRate.maxguest,
            pricemodel: channelRate.model,
            roomId: connection.roomId,
            propertyId: connection.propertyId,
            rental_id: connection.rentalId,
            maxPersons: channelRate.max_per,
            name: channelRate.name,
          }))
        )
    }

    return this.checkBeforePushRates(rental, connection).pipe(
      filter(isSomething),
      switchMap((categories) => createAction(categories as any[]))
    )
  }

  pushRatesForAPIConnection(channel: Channel, rental: Rental, connection: Connection): Observable<any> {
    return this.getPushRatesForAPIConnectionPayload(channel, rental, connection).pipe(
      filter((t) => !!t),
      tap((payload) => {
        this.store.dispatch(
          PushRatesToConnection({
            connId: connection.id,
            channelId: connection.channelId,
            channelName: channel.name,
            data: payload,
          })
        )
      })
    )
  }

  linkRental(channelName: ChannelNameTokens, connection: ConnectionView, help?: string) {
    // TV4-6111: the '....' placeholder row (fabricated when the channel returned no rooms)
    // must never be linked -- linkRental rejects it server-side. Block the flow before the
    // rental picker so every Link entry point (row menus, drawer, wizards) is covered.
    if (connection.isPlaceholderRoom) {
      this.alertDialog.open({
        title: $localize`No rooms on this connection yet.`,
        body: $localize`This row is a placeholder shown while the channel has not returned any
          rooms for the property. Refresh the room list and link a real room once one appears.`,
      })
      return EMPTY
    }
    return this.store.pipe(
      selectOnce(selectAvailableRentalForNewAPIConnection, {
        id: getRealChannelId(connection.channelId),
        selected: connection.rentalId,
      }),
      switchMap((rentals) =>
        this.selectRentalDialog.open({
          title: $localize`Select Rental`,
          help:
            help ||
            $localize`Please select the AdvanceCM rental you would like to link to this property. Linking allows you to synchronize your rental with
        this property. You can unlink this property later if you wish.`,
          selectedId: connection.rentalId,
          rentals,
        })
      ),
      filter((res) => isSomething(res)),
      tap((rental: Rental) => {
        this.store.dispatch(
          LinkConnectionWithRental({
            connId: connection.id,
            data: {
              rentalId: rental.id,
              channelId: connection.channelId,
              propertyId: connection.propertyId,
              roomId: connection.roomId,
            },
            channelName: channelName,
          })
        )
      })
    )
  }

  unlinkRental(channelName: ChannelNameTokens, connection: ConnectionView) {
    this.confirmDialog
      .confirm({
        title: $localize`Unlink this Property?`,
        body: $localize`This will unlink the rental associated with this property. <br/>Are you sure you want to unlink?`,
      })
      .subscribe(() => {
        this.store.dispatch(
          UnlinkConnectionWithRental({
            connId: connection.id,
            data: {
              propertyId: connection.propertyId,
              rentalId: connection.rentalId,
              roomId: connection.roomId,
              channelId: connection.channelId,
            },
            channelName: channelName,
          })
        )
      })
  }

  unlinkRentals(connections: ConnectionView[]) {
    this.confirmDialog
      .confirm({
        title: $localize`Unlink selected Properties?`,
        body: $localize`This will unlink the rental associated with selected properties. <br/>Are you sure you want to unlink?`,
      })
      .subscribe(() => {
        this.store.dispatch(
          UnlinkConnectionsWithRental({
            items: connections.map((connection) => ({
              connId: connection.id,
              data: {
                propertyId: connection.propertyId,
                rentalId: connection.rentalId,
                roomId: connection.roomId,
                channelId: connection.channelId,
              },
              channelName: connection.channel.name,
            })),
          })
        )
      })
  }

  refreshImportedCalendar(connection: ConnectionView) {
    this.store.dispatch(
      ManualRefreshImportedCalendarConnection({
        connId: connection.id,
        data: {
          name: connection.name,
          calendar: connection.url,
          rentalid: connection.rentalId,
          sendto: connection.sendto || R.path(['rental', 'email'], connection),
        },
      })
    )
  }

  refreshImportedCalendars(connections: ConnectionView[]) {
    this.store.dispatch(
      ManualRefreshImportedCalendarConnections({
        payloads: R.map(
          (connection) => ({
            connId: connection.id,
            data: {
              name: connection.name,
              calendar: connection.url,
              rentalid: connection.rentalId,
              sendto: connection.sendto || R.path(['rental', 'email'], connection),
            },
          }),
          connections
        ),
      })
    )
  }

  removeImportedCalendar(connection: ConnectionView) {
    this.confirmDialog
      .confirmExtra({
        title: $localize`Delete this Calendar?`,
        body: $localize`Are you sure you want to delete this calendar?`,
        extra: $localize`Delete all associated events from your calendar?`,
      })
      .subscribe(({ isChecked }) => {
        this.store.dispatch(
          DeleteImportedCalendarConnection({
            connId: connection.id,
            rentalId: connection.rentalId,
            removeAllEvents: isChecked,
          })
        )
      })
  }

  removeExportedCalendar(connection: ConnectionView) {
    this.confirmDialog
      .confirm({
        title: $localize`Delete this iCal connection?`,
        body: $localize`Are you sure you want to delete this iCal connection? Deleting the connection does not remove the AdvanceCM iCal URL
      from your channel. If you saved the AdvanceCM URL in your channel please log into your channel dashboard and also delete it there.`,
      })
      .subscribe(() => {
        this.store.dispatch(DeleteExportedCalendarConnection({ id: connection.id }))
      })
  }

  removeCalendars(connections: ConnectionView[]) {
    const imported = connections.filter((t) => t.isImported)
    const exported = connections.filter((t) => t.isExported)

    if (!imported.length) {
      this.confirmDialog
        .confirm({
          title: $localize`Delete selected calendars?`,
          body: $localize`Are you sure you want to delete selected calendars?`,
        })
        .subscribe(() => {
          this.store.dispatch(DeleteCalendarConnections({ exported: exported.map((t) => t.id) }))
        })
    } else {
      this.confirmDialog
        .confirmExtra({
          title: $localize`Delete selected calendars?`,
          body: $localize`Are you sure you want to delete selected calendars?`,
          extra: $localize`Delete all associated events from your imported calendars?`,
        })
        .subscribe(({ isChecked }) => {
          this.store.dispatch(
            DeleteCalendarConnections({
              exported: exported.map((t) => t.id),
              imported: {
                items: imported.map((t) => ({ connId: t.id, rentalId: t.rentalId })),
                removeAllEvents: isChecked,
              },
            })
          )
        })
    }
  }
}
